import { observable, computed, action } from 'mobx'
import { apis, request } from "@/utils/net"

// 开票接口的场景值，1在线人工开票，2纸票审核开票，3二维码小票开票，4酒店开票
const INVOICE_SCENE = 2
const defaultPageSize = 8
const defaultInvoiceData = {
    client_info: {}, // 开票方（商户）信息
    buyer_info: {}, // 购方信息
    goods_info: [], // 商品（开票项目）信息
    invoice_info: {}, // 发票信息
    act_button: { // 操作按钮，0不展示，1展示
        can_do_and_print: 1, // 开票并打印
        can_refuse: 0, // 拒绝开票
        can_try: 0, // 重试开票
        can_print: 0, // 纸票打印
        add_invoice: 0, // 新增发票
        can_red: 0, // 红冲
        can_invalid: 0, // 作废
    },
}

class Store {
    // 缓存当前显示的发票数据
    _invoiceData = {}
    // 抬头类型，1个人，2企业
    // 这里独立维护，避免在切换抬头类型时，导致 invoiceData 变化，而更新了开票组件的页面数据
    @observable titleType = 2
    // 发票数据
    @observable.shallow invoiceData = null
    // 新增发票初始化信息
    @observable.shallow businessInfo = {}
    // 待开具发票列表
    @observable.shallow invoiceRecords = {
        list: [],
        page: 1,
        pageSize: defaultPageSize,
        total: 0
    }
    // 已开具发票列表
    @observable.shallow openedInvoiceRecords = {
        list: [],
        page: 1,
        pageSize: defaultPageSize,
        total: 0
    }

    @action setInvoiceData(invoiceData) {
        if (!invoiceData || this._invoiceData === invoiceData) {
            return false
        }

        this._invoiceData = invoiceData
        this.invoiceData = {...invoiceData}
    }

    @action clearInvoiceData() {
        this.invoiceData = null
    }

    @action fetchInvoiceRecords(status, keyword = '', currentPage) {
        const dataKey = status === 1 ? 'invoiceRecords' : 'openedInvoiceRecords'
        const pageIndex = currentPage || this[dataKey].page
        return request({
            url: apis.paperInvoiceList,
            method: 'post',
            data: {
                keyword,
                status,
                page: pageIndex,
                size: this[dataKey].pageSize,
            }
        }).then(res => {
            let { list = [], page = 1, total_count: total = 0 } = res
            // 在数据为空时，重置 page, total 的值
            if (!list.length) {
                page = 1
                total = 0
            }
            this[dataKey] = {
                ...this[dataKey],
                list,
                page,
                total: +total
            }
            return res
        })
    }

    /**
     * fetchInvoiceCode
     * 待审核的发票，获取下一张纸票的发票代码和号码，用于页面显示
     * 该请求去除loading效果，并且不处理异常
     * @param  {String} storeID      门店ID
     * @param  {String} invoiceStyle 发票类型 1 普票, 2 专票, 6 卷票
     */
    @action fetchInvoiceCode(storeID, invoiceStyle) {
        return request({
            url: apis.paperGetInvoiceCode,
            method: 'post',
            data: {
                store_id: storeID,
                invoice_style: invoiceStyle
            },
            onLoadStart() {},
            unforeseen() {}
        }).then(res => {
            const { ticket_sn = '', ticket_code = '' } = res
            // 更新发票代码、发票号码到 this.invoiceData.invoice_info
            if (ticket_sn || ticket_code) {
                const invoiceData = {
                    ...this.invoiceData,
                    invoice_info: {
                        ...this.invoiceData.invoice_info,
                        ticket_sn,
                        ticket_code
                    }
                }
                this.setInvoiceData(invoiceData)
            }
        })
    }

    @action invoice(invoiceData, scene) {
        return request({
            url: apis.paperInvoice,
            method: 'post',
            data: {
                ...invoiceData,
                scene: scene || INVOICE_SCENE
            }
        })
    }

    @action retry(orderSn) {
        return request({
            url: apis.paperRetry,
            method: 'post',
            data: {
                order_sn: orderSn
            }
        })
    }

    @action print(orderSn) {
        return request({
            url: apis.paperPrint,
            method: 'post',
            data: {
                order_sn: orderSn
            }
        })
    }

    @action reject(orderSn, reason) {
        return request({
            url: apis.paperReject,
            method: 'post',
            data: {
                order_sn: orderSn,
                audit_msg: reason
            }
        })
    }

    /**
     * 新增发票
     * @param type 发票类型 1 普票，2 专票，6 卷票
     */
    @action addInvoiceData(type) {
        const invoiceInfo = this.businessInfo.invoice_info
        this.invoiceData = {
            goods_info: [],
            client_info: this.businessInfo.client_info,
            invoice_info: {
                ...invoiceInfo,
                payee: type === '6' ? invoiceInfo.username : invoiceInfo.payee,
                invoice_style: type
            },
            buyer_info: {
                title_type: this.titleType // 抬头类型
            },
            act_button: {
                can_do_and_print: 0, // 开票并打印
                can_refuse: 0, // 拒绝开票
                can_try: 0, // 重试开票
                can_print: 0, // 纸票打印
                add_invoice: 1, // 新增发票
            },
        }
        this._invoiceData = this.invoiceData
    }

    // 获取开票方（商户）信息
    @action fetchMerchantInfo() {
        return request({
            url: apis.getMerchantInfo,
            unforeseen: () => {}
        }).then(res => {
            this.businessInfo = {
                ...res,
                client_info: {
                    name: res.name,
                    tax_code: res.tax_code,
                    phone: res.sell_phone,
                    bank_name: res.sell_bank_name,
                    bank_account: res.sell_bank_account,
                    address: res.sell_address,
                },
                invoice_info: { // 发票信息
                    payee: res.payee,
                    reviewer: res.reviewer,
                    drawer: res.drawer,
                    // 卷票新增，收款员
                    // 管理员，取登录用户，非管理员，取门店员工姓名
                    username: res.username,
                    // 机器编码，税盘号
                    tax_num: res.tax_num
                }
            }
            return res
        })
    }

    /**
     * 改变抬头类型
     * @param type 1 个人， 2 企业
     */
    @action changeTitleType(type) {
        this.titleType = type
    }

    /**
     * 红冲
     * @param orderSn
     * @param remark
     * @return {*}
     */
    @action redRush(orderSn, remark) {
        return request({
            url: apis.postRedRecord,
            method: 'post',
            data: {
                order_sn: orderSn,
                remark
            }
        })
    }

    /**
     * 作废
     * @param orderSn
     * @return {*}
     */
    @action invoiceInvalid(orderSn) {
        return request({
            url: apis.invoiceInvalid,
            method: 'post',
            data: {
                order_sn: orderSn
            }
        })
    }
}

export default new Store()
